/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import {
  usePathMatch,
  useRequestCommunitiesAdminCommunity,
  useRequestMe,
} from "@packages/sdk";
import type { SidebarProps } from "@packages/ui";
import {
  CommunitySelector,
  cssMediaQueryMinWidthTablet,
  ReportedCounter,
  Sidebar,
  SidebarTab,
} from "@packages/ui";
import { useParams } from "next/navigation";

import { getBasePADUrl, PAD_TABS } from "../../../lib";
import { WEB_APP_FULL_SCREEN_ROUTES } from "../../../lib/constants/routes.constants";

type ParishAdminDashboardSidebarProps = Omit<
  SidebarProps,
  "children" | "state"
>;

const sidebarStyles: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};

const SIDEBAR_HIDDEN_ROUTES = [
  "/admin/:communityId/events/:id",
  ...WEB_APP_FULL_SCREEN_ROUTES,
];

export const ParishAdminDashboardSidebar = ({
  ...props
}: ParishAdminDashboardSidebarProps) => {
  const params = useParams<{ communityId: string }>();
  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: parseInt(params.communityId),
  });
  const { query: meQuery } = useRequestMe();

  const matcher = usePathMatch();
  const isSidebarHidden = SIDEBAR_HIDDEN_ROUTES.some((route) => matcher(route));

  if (isSidebarHidden) return null;

  const parishAdminBaseUrl = getBasePADUrl({
    communityId: parseInt(params.communityId),
  });

  const activeTab = PAD_TABS.find((tab) =>
    matcher(`${parishAdminBaseUrl}/${tab.path}`),
  );

  const activeTabSidebarState = activeTab?.sidebarState ?? "expanded";

  return (
    <Sidebar state={activeTabSidebarState} css={sidebarStyles} {...props}>
      <CommunitySelector triggerState={activeTabSidebarState} />
      {PAD_TABS.map(
        ({
          sidebarState,
          path,
          Icon,
          getHidden,
          subTabs,
          message,
          getMessage,
          ...props
        }) => (
          <SidebarTab
            key={path}
            href={`${parishAdminBaseUrl}/${path}`}
            state={activeTabSidebarState}
            message={message ?? getMessage(communityQuery.data)}
            analyticsEvent={{
              event: HallowAnalyticsEvent.TappedAdminDashboardTab,
              properties: {
                tab: path,
              },
            }}
            actionRight={
              path === "reported" && (
                <ReportedCounter communityId={parseInt(params.communityId)} />
              )
            }
            icon={<Icon fill="currentColor" />}
            hidden={
              getHidden
                ? communityQuery.data && meQuery.data
                  ? getHidden({
                      community: communityQuery.data,
                      user: meQuery.data,
                    })
                  : true
                : false
            }
            subTabs={subTabs?.map((subTab) => ({
              analyticsEvent:
                path === "settings"
                  ? {
                      event: HallowAnalyticsEvent.TappedCommunitySettings,
                      properties: {
                        option: subTab.path.replace("-", "_"),
                      },
                    }
                  : {
                      event: HallowAnalyticsEvent.TappedAdminDashboardTab,
                      properties: {
                        tab: subTab.path,
                      },
                    },
              href: `${parishAdminBaseUrl}/${path}/${subTab.path}`,
              icon: <subTab.Icon fill="currentColor" />,
              getHidden: subTab.getHidden,
              message: subTab.message,
            }))}
            {...props}
          />
        ),
      )}
    </Sidebar>
  );
};
